import React, { useCallback, useState } from 'react';
import { connect } from 'react-redux';
import { setActionsModalOpen, setSelectedGroup } from '@reducers/machinesV2';
import { setSelectedMachine } from '@reducers/machines';
import ColorWrapper from '@styles/colors';
import { useMemo, useRef } from 'react';
import { notchTop, scale } from '@components/frame';
import { useHandleNavigationMachinesHeader } from '@v2/pages/machines/hooks/useHandleNavigationMachineHeader';
import i18n from 'i18n-js';
import { Machine } from '@interfaces/machines';
import { Animated, TouchableOpacity, View } from 'react-native';
import Svg from '@styles/icons/svg';
import Header from '@components/machines/components/header/List';
import MachineActionsModal from '@components/machines/components/modals/machineActionsModal';
import {
  ALL_MACHINES_OBJECT,
  BACK_BUTTON_OFFSET_LEFT,
  PHONE_DETAILS_HEADER_GAP,
  PHONE_DETAILS_HEADER_MARGIN_TOP,
  PHONE_LIST_HEADER_MARGIN_TOP,
} from '../../../../common/constants/base.constants';
import { useHandleParams } from '@v2/pages/machines/hooks/useHandleParams';
import BackButton from '@components/common/backButton';
import { isPhone } from '@helpers';
import { useFocusEffect } from '@react-navigation/native';

const MachinesScreenMobileHeaderComponent = ({
  selectedGroup,
  setSelectedGroup,
  selectedItem,
  C,
  machines,
  machineStatistics,
  setSelectedMachine,
  setActionsModalOpen,
  actionsModalOpen,
  noSelected,
}) => {
  const fadeAnim = useRef(new Animated.Value(0)).current;
  const animationDuration = scale(100);

  const [isFocused, setIsFocused] = useState(false);

  const { isNowSubScreen, isMachinesScreen, shouldUseSystemBackgroundOnPhone, shouldDisplayAllMachinesButton } = useHandleNavigationMachinesHeader();
  const { isAllSelected, id } = useHandleParams();

  const title = !!id && isAllSelected ? i18n.t('Machines') : selectedItem?.shortName;

  const machinesList: Machine[] = useMemo(() => {
    return isNowSubScreen ? machines : machineStatistics?.data;
  }, [machines, isNowSubScreen, machineStatistics]);

  const indexOfSelectedMachine = useMemo(() => {
    return machinesList?.findIndex((m: Machine) => m?.id === selectedItem?.id) ?? -1;
  }, [machinesList, selectedItem]);

  const onPressNextOrPrev = (action: 'prev' | 'next') => {
    if (indexOfSelectedMachine === -1) return;

    let newSelectedMachineIndex: number | null;
    switch (action) {
      case 'prev': {
        newSelectedMachineIndex = indexOfSelectedMachine === 0 ? machinesList.length - 1 : indexOfSelectedMachine - 1;
        break;
      }
      case 'next': {
        newSelectedMachineIndex = indexOfSelectedMachine === machinesList.length - 1 ? 0 : indexOfSelectedMachine + 1;
        break;
      }
    }

    const newSelectedMachine = machinesList[newSelectedMachineIndex];
    if (!newSelectedMachine) return;

    setSelectedMachine(newSelectedMachine);
  };

  const machineSelected = (!!id && !isAllSelected) || noSelected;

  const bgColor = isPhone && shouldUseSystemBackgroundOnPhone ? 'Background/Light/System' : 'Background/Light/Detail';

  useFocusEffect(
    useCallback(() => {
      setIsFocused(true);
      return () => setIsFocused(false);
    }, [])
  );

  return (
    <View style={[{ backgroundColor: C(bgColor) }]}>
      {machineSelected && (
        <View
          style={{
            flexDirection: 'row',
            alignItems: 'center',
            justifyContent: 'space-between',
            marginTop: scale(PHONE_DETAILS_HEADER_MARGIN_TOP + notchTop),
            paddingLeft: scale(isMachinesScreen ? BACK_BUTTON_OFFSET_LEFT : 0),
            paddingRight: scale(isMachinesScreen ? 15 : 0),
          }}
        >
          <BackButton
            style={{ paddingHorizontal: 0 }}
            text="Machines"
            onPress={() => {
              setSelectedMachine(ALL_MACHINES_OBJECT);
            }}
          />
          {!noSelected && (
            <View style={{ flexDirection: 'row', alignItems: 'center', gap: scale(24) }}>
              <TouchableOpacity onPress={() => onPressNextOrPrev('prev')}>
                <Svg
                  icon="chevronBottomLight"
                  width={scale(24)}
                  height={scale(24)}
                  color={C('Button/Light/Secondary Text')}
                  transform="rotate(180)"
                />
              </TouchableOpacity>
              <TouchableOpacity onPress={() => onPressNextOrPrev('next')}>
                <Svg icon="chevronBottomLight" width={scale(24)} height={scale(24)} color={C('Button/Light/Secondary Text')} />
              </TouchableOpacity>
              <TouchableOpacity onPress={() => setActionsModalOpen(true)}>
                <Svg icon="more" width={scale(24)} height={scale(24)} color={C('Button/Light/Secondary Text')} />
              </TouchableOpacity>
            </View>
          )}
        </View>
      )}
      <Header
        {...{
          text: title,
          fadeAnim,
          marginTop: scale(
            (shouldDisplayAllMachinesButton && isAllSelected ? PHONE_LIST_HEADER_MARGIN_TOP : PHONE_DETAILS_HEADER_GAP) +
              (machineSelected ? 0 : notchTop)
          ),
          animationDuration,
          selectedGroup,
          setSelectedGroup,
          bg: bgColor,
          ...(!isAllSelected ? { fontType: 'Title 2' } : {}),
          ...(!isAllSelected ? { fontWeight: 700 } : {}),
        }}
        key="header"
      />
      {isFocused && <MachineActionsModal panelViewMode={false} right={9} top={50 + notchTop} />}
    </View>
  );
};

export const MachinesScreenMobileHeader = connect(
  ({ machinesV2: { selectedGroup, actionsModalOpen }, machines: { machines, machineStatistics, selectedItem } }) => ({
    selectedGroup,
    machines,
    machineStatistics,
    actionsModalOpen,
    selectedItem,
  }),
  {
    setSelectedGroup,
    setSelectedMachine,
    setActionsModalOpen,
  }
)(ColorWrapper(MachinesScreenMobileHeaderComponent));
